import { ARIZONA_COUNTY_SAMPLE_DATA } from "./sample-data.js";

export const COUNTY_GEOMETRY_URL = "/data/arizona-counties.geojson";

export function normalizeCountyFips(value) {
  const candidate = typeof value === "number" && Number.isInteger(value)
    ? String(value)
    : typeof value === "string"
      ? value.trim()
      : "";
  if (!/^\d{1,5}$/.test(candidate)) {
    throw new TypeError("County FIPS must contain between one and five digits.");
  }
  return candidate.padStart(5, "0");
}

export function validateCountySampleRows(rows) {
  if (!Array.isArray(rows)) {
    throw new TypeError("County sample data must be an array.");
  }
  const seenFips = new Set();
  return Object.freeze(rows.map((county, index) => {
    if (!county || typeof county !== "object" || Array.isArray(county)) {
      throw new TypeError(`County sample row at index ${index} must be an object.`);
    }
    const fips = normalizeCountyFips(county.fips);
    if (seenFips.has(fips)) {
      throw new RangeError(`County sample FIPS ${fips} must be unique.`);
    }
    seenFips.add(fips);
    const sickCount = nonNegativeWholeNumber(county.sick_count, `sample[${index}].sick_count`);
    const totalCount = nonNegativeWholeNumber(county.total_count, `sample[${index}].total_count`);
    if (sickCount > totalCount) {
      throw new RangeError(`sample[${index}].sick_count cannot exceed total_count.`);
    }
    return Object.freeze({
      fips,
      county: requiredCountyName(county.county, index),
      sickCount,
      totalCount,
    });
  }));
}

export function validateCountyGeometry(geometry) {
  if (!geometry || geometry.type !== "FeatureCollection" || !Array.isArray(geometry.features)) {
    throw new TypeError("Arizona county geometry must be a GeoJSON FeatureCollection.");
  }
  geometry.features.forEach((feature, index) => {
    if (!feature || feature.type !== "Feature" || !feature.geometry || !feature.properties) {
      throw new TypeError(`County geometry feature at index ${index} is malformed.`);
    }
    normalizeCountyFips(feature.properties.GEOID);
    if (typeof feature.properties.NAME !== "string" || feature.properties.NAME.trim() === "") {
      throw new TypeError(`County geometry feature at index ${index} has no name.`);
    }
  });
  return geometry;
}

export function joinCountyCountsToGeometry(geometry, sampleRows = ARIZONA_COUNTY_SAMPLE_DATA) {
  validateCountyGeometry(geometry);
  const validatedRows = validateCountySampleRows(sampleRows);
  const rowsByFips = new Map(validatedRows.map((county) => [county.fips, county]));
  const knownFips = new Set();

  const features = geometry.features.map((feature) => {
    const fips = normalizeCountyFips(feature.properties.GEOID);
    knownFips.add(fips);
    const row = rowsByFips.get(fips);
    const countyName = feature.properties.NAME.trim();
    return {
      ...feature,
      properties: {
        ...feature.properties,
        countyFips: fips,
        countyLabel: countyName.replace(/\s+County$/i, ""),
        countyName,
        hasCountyData: Boolean(row),
        sickCount: row?.sickCount ?? 0,
        totalCount: row?.totalCount ?? 0,
      },
    };
  });

  const unknownFips = validatedRows
    .filter(({ fips }) => !knownFips.has(fips))
    .map(({ fips }) => fips);

  return Object.freeze({
    featureCollection: {
      type: "FeatureCollection",
      features,
    },
    unknownFips: Object.freeze(unknownFips),
  });
}

export async function fetchCountyGeometry({
  fetchImpl = fetch,
  signal,
  url = COUNTY_GEOMETRY_URL,
} = {}) {
  const response = await fetchImpl(url, { headers: { Accept: "application/geo+json, application/json" }, signal });
  if (!response.ok) {
    throw new Error(`County geometry request failed with HTTP ${response.status}.`);
  }
  const payload = await response.json();
  validateCountyGeometry(payload);
  return payload;
}

function nonNegativeWholeNumber(value, field) {
  if (!Number.isInteger(value) || value < 0) {
    throw new TypeError(`${field} must be a non-negative whole number.`);
  }
  return value;
}

function requiredCountyName(value, index) {
  if (typeof value !== "string" || value.trim() === "") {
    throw new TypeError(`County sample row at index ${index} has no name.`);
  }
  return value.trim();
}
